/**
 * BasicInfoTab — Device details, status/sensor display, and event location map.
 *
 * Conditionally renders based on device type:
 *   - Inovonics: Device Details + Status & Sensors (temp, humidity, battery)
 *   - Palcare:   Device Details (+ Line Profile field) + Event Location Map
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {LayersControl, MapContainer, Marker, Popup} from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { convertIsoToLocal } from '../../../../libs.jsx';
import { GOOGLE_MAPS_API_KEY as GOOGLE_API_KEY } from '../../../../constants/mapsConfig.js';
import { MUTED_BADGE } from '../../../../utils/deviceStatus.js';

/**
 * HoverTip — wraps anything and explains it on hover.
 *
 * Used for two jobs here: explaining why a disabled control is disabled, and
 * explaining a status badge whose label cannot carry the whole story.
 *
 * The hover lives on this wrapper, never as a `title` on the control: a disabled
 * button fires no pointer events, so a title there would never surface. Chrome
 * still dispatches the event to the ancestor, which is why the wrapper works.
 *
 * Passing no message renders the child bare, with no wrapper handlers. Note the
 * early return sits AFTER the hook — `message` changes at runtime as alerts start
 * and clear, and returning before the hook would make the hook count vary, which
 * React rejects with "Rendered fewer hooks than expected".
 */
const HoverTip = ({ message, children, align = 'right' }) => {
  const [open, setOpen] = useState(false);

  if (!message) return children;

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
      {open ? (
        <span
          className="na-type-popover"
          role="tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            /* Anchored to whichever edge keeps the tip inside the card: the header
               buttons sit hard right, the status badge hard left. */
            ...(align === 'left' ? { left: 0 } : { right: 0 }),
            /* width, not just max-width. An absolutely positioned box shrink-to-fits
               against its containing block, and the wrapper is only as wide as the
               control it hugs — 63px for the Edit button — so the tip collapsed to
               one word per line and max-width never engaged. max-content ignores the
               containing block; max-width then caps and wraps it. */
            width: 'max-content',
            maxWidth: '280px',
            whiteSpace: 'normal',
            zIndex: 'var(--z-popover)',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
            {message}
          </span>
        </span>
      ) : null}
    </span>
  );
};

const BasicInfoTab = ({
  basicData, deviceName, description, serialNum, modelName,
  deviceTypeName, deviceCategory, behaviorText,
  isAreaDevice, areaId, areaName, residentId, residentName,
  lastRepeater, lastAlarm, lastActive,
  deviceTypeId, currentTemp, currentHumidity, currentBattery,
  deviceImg, isActive, loading, editRights, onEdit, onUnassign,
  /* Palcare-specific props */
  isPalcare, dspProfileName, eventData, campusLat, campusLong, tzOffset,
  /* Alarm suppression — this tab only opens the dialog. The dialog owns the
     duration choice, and the "currently suppressed" banner is rendered by the page
     so it survives a tab switch (DeviceProfilePage.jsx).

     `alertActive` blocks three controls while the device is alarming: Suppress,
     Edit and Unassign. All three can stop the device's clear signal being
     processed — Edit because it can deactivate the device or move it to another
     area or resident mid-alarm.

     Suppress additionally requires the device to be active: see deviceActive. */
  deviceMuted, alertActive, onOpenMute,
  /* Active AND suppressed. Derived on the page so the header badge and this one
     cannot disagree — see showsMuted() in utils/deviceStatus.js. */
  showMuted,
}) => {
  const alarmBlockedMsg = alertActive
    ? 'This device has an active alarm. Changing it now could stop the clear signal being processed. Wait until the alert is resolved.'
    : '';

  /* An unassigned device is already deactivated and cannot raise an alarm, so
     there is nothing to suppress — the control is hidden rather than disabled,
     since unlike an active alarm this is not a state that clears on its own.

     Coerced through Number() rather than tested for truthiness: is_active arrives
     from a MySQL tinyint, and the string "0" is truthy in JS. The page normalizes
     it to 1/0 today, so this only guards against that changing. */
  const deviceActive = Boolean(Number(isActive));

  // Battery display helper (Inovonics only)
  const getBatteryInfo = (voltage) => {
    if (!voltage) return null;
    const v = parseFloat(voltage);
    if (v >= 2.8) return { level: 'full', label: `Full ${v}V`, icon: '🔋' };
    if (v === 2.7) return { level: 'good', label: `Good ${v}V`, icon: '🔋' };
    if (v >= 2.5) return { level: 'low', label: `Low ${v}V`, icon: '🪫' };
    return { level: 'critical', label: `Critical ${v}V`, icon: '⚠' };
  };

  const battery = !isPalcare ? getBatteryInfo(currentBattery) : null;

  // Event markers for Palcare map
  const eventMarkers = useMemo(() => {
    if (!isPalcare || !eventData?.length) return [];
    return eventData.filter(
      (evt) => evt.latitude && evt.longitude
               && evt.latitude !== 0 && evt.longitude !== 0
    );
  }, [isPalcare, eventData]);

  // Map center: first event with coords, else campus center
  const mapCenter = useMemo(() => {
    if (eventMarkers.length > 0) {
      return [eventMarkers[0].latitude, eventMarkers[0].longitude];
    }
    return [campusLat || 0, campusLong || 0];
  }, [eventMarkers, campusLat, campusLong]);

  return (
    <>
      {/* Device Details */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Device Details</h3>
          <div className="pp-section__actions">
            {editRights && (
              <>
                <HoverTip message={alarmBlockedMsg}>
                  <button
                    className="dp-btn dp-btn--secondary dp-btn--sm"
                    onClick={onEdit}
                    disabled={alertActive}
                  >
                    ✎ Edit
                  </button>
                </HoverTip>
                {!!isActive && (
                  <HoverTip message={alarmBlockedMsg}>
                    <button
                      className="dp-btn dp-btn--danger dp-btn--sm"
                      onClick={onUnassign}
                      disabled={alertActive}
                    >
                      Unassign
                    </button>
                  </HoverTip>
                )}
              </>
            )}
          </div>
        </div>
        <div className="pp-section__body">
          <div style={{ display: 'flex', gap: 'var(--space-5)' }}>
            {/* Device image */}
            {deviceImg && (
              <div className="ip-device-image">
                <img
                  className="ip-device-image__img"
                  src={`/static/image/${deviceImg}`}
                  alt={deviceName}
                />
              </div>
            )}

            {/* Field grid */}
            <div className="pp-field-grid" style={{ flex: 1 }}>
              <div className="pp-field">
                <span className="pp-field__label">Device Name</span>
                <span className="pp-field__value">{deviceName || '—'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Category</span>
                <span className="pp-field__value">{deviceCategory || '—'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Device Type</span>
                <span className="pp-field__value">{deviceTypeName || '—'}</span>
              </div>
              {/* Behavior (Inovonics) / Line Profile (Palcare) */}
              {isPalcare ? (
                <div className="pp-field">
                  <span className="pp-field__label">Line Profile</span>
                  <span className="pp-field__value">{dspProfileName || 'No Profile'}</span>
                </div>
              ) : (
                <div className="pp-field">
                  <span className="pp-field__label">Behavior</span>
                  <span className="pp-field__value">{behaviorText || 'Standard'}</span>
                </div>
              )}
              <div className="pp-field">
                <span className="pp-field__label">Serial Number</span>
                <span className="pp-field__value pp-field__value--mono">{serialNum || '—'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Model Number</span>
                <span className="pp-field__value">{modelName || '—'}</span>
              </div>
              {!isAreaDevice && (
                <div className="pp-field">
                  <span className="pp-field__label">Room</span>
                  <span className="pp-field__value">
                    {areaId ? (
                      <Link to={`/community/areas/${areaId}`} className="pp-field__value--link">
                        {areaName || '—'}
                      </Link>
                    ) : '—'}
                  </span>
                </div>
              )}
              <div className="pp-field">
                <span className="pp-field__label">Assigned To</span>
                <span className="pp-field__value">
                  {isAreaDevice ? (
                    areaId ? (
                      <Link to={`/community/areas/${areaId}`} className="pp-field__value--link">
                        {areaName || '—'}
                      </Link>
                    ) : '—'
                  ) : (
                    residentId ? (
                      <Link to={`/community/residents/${residentId}`} className="pp-field__value--link">
                        {residentName || '—'}
                      </Link>
                    ) : '—'
                  )}
                </span>
              </div>
              {isAreaDevice ? (
                <div className="pp-field">
                  <span className="pp-field__label">Location in Room</span>
                  <span className="pp-field__value">{description || '—'}</span>
                </div>) : <></>
              }
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Inovonics: Status & Sensors                                        */}
      {/* ------------------------------------------------------------------ */}
      {!isPalcare && (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Status & Sensors</h3>
            {/* Suppression lives in this card because it already reports device
                state and when it last alarmed. Hidden while suppressed — the
                page's banner owns that state and carries the resume button, so
                showing both would offer two controls for one setting. */}
            {/*<div className="pp-section__actions">*/}
            {/*  {editRights && !deviceMuted && deviceActive ? (*/}
            {/*    <HoverTip message={alarmBlockedMsg}>*/}
            {/*      <button*/}
            {/*        type="button"*/}
            {/*        className="dp-btn dp-btn--secondary dp-btn--sm"*/}
            {/*        onClick={onOpenMute}*/}
            {/*        disabled={alertActive}*/}
            {/*        title={alertActive ? undefined : 'Temporarily stop this device producing alarms'}*/}
            {/*      >*/}
            {/*        <i className="fe fe-bell-off" aria-hidden="true" style={{ marginRight: 6 }} />*/}
            {/*        Suppress Alarms*/}
            {/*      </button>*/}
            {/*    </HoverTip>*/}
            {/*  ) : null}*/}
            {/*</div>*/}
          </div>
          <div className="pp-section__body">
            <div className="pp-field-grid pp-field-grid--3col">
              <div className="pp-field">
                <span className="pp-field__label">Last Repeater</span>
                <span className="pp-field__value">{lastRepeater || 'No Record'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Last Alarmed</span>
                <span className="pp-field__value">
                  {lastAlarm ? convertIsoToLocal(lastAlarm) : 'No Record'}
                </span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Last Active</span>
                <span className="pp-field__value">
                  {lastActive ? convertIsoToLocal(lastActive) : 'No Record'}
                </span>
              </div>

              {/* Temperature (device types 1, 28) */}
              {[1, 28].includes(deviceTypeId) && (
                <div className="pp-field">
                  <span className="pp-field__label">Last Temperature</span>
                  <span className="pp-field__value">
                    {currentTemp ? (
                      <span className="ip-sensor-value">
                        {currentTemp}<span className="ip-sensor-value__unit">{'\u00B0'}F</span>
                      </span>
                    ) : '—'}
                  </span>
                </div>
              )}

              {/* Humidity (device type 28) */}
              {[28].includes(deviceTypeId) && (
                <div className="pp-field">
                  <span className="pp-field__label">Last Humidity</span>
                  <span className="pp-field__value">
                    {currentHumidity ? (
                      <span className="ip-sensor-value">
                        {currentHumidity}<span className="ip-sensor-value__unit">%</span>
                      </span>
                    ) : '—'}
                  </span>
                </div>
              )}

              {/* Battery (device type 1) */}
              {[1].includes(deviceTypeId) && battery && (
                <div className="pp-field">
                  <span className="pp-field__label">Battery</span>
                  <span className="pp-field__value">
                    <span className={`ip-battery ip-battery--${battery.level}`} title={battery.label}>
                      <span className="ip-battery__icon">{battery.icon}</span>
                      <span className="ip-battery__label">{battery.label}</span>
                    </span>
                  </span>
                </div>
              )}

              <div className="pp-field">
                <span className="pp-field__label">Status</span>
                <span className="pp-field__value">
                  {/* Same three states as the page header, and a hover that spells
                      out the one the label cannot: green means the device is
                      reporting, "Muted" means it still will not alert anyone. */}
                  <HoverTip message={showMuted ? MUTED_BADGE.title : ''} align="left">
                    <span
                      className={`pp-profile-banner__status pp-profile-banner__status--${
                        !isActive ? 'offline' : (showMuted ? 'muted' : 'online')}`}
                    >
                      <span className="pp-profile-banner__status-dot" />
                      {!isActive ? 'Unassigned' : (showMuted ? MUTED_BADGE.label : 'Active')}
                    </span>
                  </HoverTip>
                </span>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Palcare: Event Location Map                                        */}
      {/* ------------------------------------------------------------------ */}
      {isPalcare && (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Event Locations</h3>
            <div className="pp-section__actions">
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                {eventMarkers.length > 0
                  ? `${eventMarkers.length} event${eventMarkers.length !== 1 ? 's' : ''} with location data`
                  : 'No events with location data'}
              </span>
            </div>
          </div>
          <div className="pp-section__body pp-section__body--flush">
            <div className="ip-map-container">
              <MapContainer
                zoom={19}
                scrollWheelZoom={true}
                center={mapCenter}
                style={{ height: '100%', width: '100%' }}
              >
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Roadmap">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="roadmap" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Satellite">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="satellite" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Terrain">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="terrain" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Hybrid">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="hybrid" />
                  </LayersControl.BaseLayer>
                </LayersControl>
                {eventMarkers.map((evt, idx) => (
                  <Marker
                    key={`event-marker-${evt.event_id || idx}`}
                    position={[evt.latitude, evt.longitude]}
                  >
                    <Popup>
                      <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                        <strong>Event Time:</strong> {convertIsoToLocal(evt.event_datetime_local)}<br />
                        <strong>Event Clear:</strong> {evt.ack_datetime_local ? convertIsoToLocal(evt.ack_datetime_local) : '—'}<br />
                        <strong>Response:</strong> {evt.response_time ?? '—'} Minutes<br />
                        <strong>Reason:</strong> {evt.reason_code_description || 'No Reason'}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BasicInfoTab;
